import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import impersonationService, { ImpersonationStatus } from '../services/impersonation.service';
import { useMsal } from '@azure/msal-react';

interface ImpersonationContextType {
  impersonationStatus: ImpersonationStatus | null;
  isLoading: boolean;
  refreshStatus: () => Promise<void>;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

interface ImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
  const [impersonationStatus, setImpersonationStatus] = useState<ImpersonationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<'start' | 'stop' | null>(null);
  const { instance, accounts } = useMsal();

  const refreshStatus = async () => {
    // Only check impersonation status if user is authenticated
    if (accounts.length === 0) {
      setImpersonationStatus(null);
      return;
    }

    try {
      setIsLoading(true);
      const status = await impersonationService.getStatus();
      setImpersonationStatus(status);
    } catch (error) {
      setImpersonationStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  const startImpersonation = async (userId: string) => {
    try {
      setIsLoading(true);
      setLoadingAction('start');
      await impersonationService.startImpersonation(userId);
      await refreshStatus();
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  const stopImpersonation = async () => {
    try {
      setIsLoading(true);
      setLoadingAction('stop');
      await impersonationService.stopImpersonation();
      await refreshStatus();
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  // Check impersonation status on mount and when authentication changes
  useEffect(() => {
    if (accounts.length > 0) {
      refreshStatus();
    }
  }, [accounts.length]);

  const value: ImpersonationContextType = {
    impersonationStatus,
    isLoading,
    refreshStatus,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}

      {/* Loading Modal Overlay - Only show when actively starting/stopping, not when just refreshing status */}
      {isLoading && loadingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
          <div className="bg-panel rounded-lg shadow-xl p-8 max-w-sm mx-4">
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-900 rounded-full"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 dark:border-blue-400 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-heading mb-1">
                  {loadingAction === 'stop' ? 'Exiting Impersonation...' : 'Starting Impersonation...'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please wait while we process your request
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </ImpersonationContext.Provider>
  );
};
